// frontend/src/components/librarian/dashboard/GreetingBanner.tsx
/* Hallmark · signature: the greeting set in the serif beside a shelf of book spines
 * drawn from the theme tokens (no image asset, so it follows dark mode) and today's quote. */
import React from 'react';

interface Props {
  greeting: string;
  name: string;
  line: string;
  quote: { text: string; by: string };
}

/** Widths and heights in px; colours are the theme's, so both themes keep the shelf. */
const SPINES: ReadonlyArray<{ w: number; h: number; c: string }> = [
  { w: 12, h: 64, c: 'var(--lib-navy)' },
  { w: 16, h: 82, c: 'var(--lib-gold)' },
  { w: 10, h: 72, c: 'var(--lib-ink)' },
  { w: 18, h: 90, c: 'var(--lib-navy)' },
  { w: 14, h: 60, c: 'var(--lib-gold-text)' },
  { w: 12, h: 76, c: 'var(--lib-navy)' },
  { w: 16, h: 94, c: 'var(--lib-gold)' },
  { w: 10, h: 68, c: 'var(--lib-ink-muted)' },
  { w: 14, h: 84, c: 'var(--lib-navy)' },
  { w: 12, h: 58, c: 'var(--lib-gold-text)' },
];

const GreetingBanner: React.FC<Props> = ({ greeting, name, line, quote }) => (
  <section
    aria-label="Welcome"
    className="grid gap-6 md:grid-cols-[minmax(0,1fr)_auto] items-end rounded-lg border border-[var(--lib-hairline)] bg-[var(--lib-surface-2)] px-6 pt-6 pb-6 md:px-8 md:pt-8 md:pb-0 overflow-hidden"
  >
    <div className="min-w-0 md:pb-8">
      <h2 className="font-serif text-[34px] md:text-[42px] leading-[1.1] text-[var(--lib-ink)]">
        {greeting}, {name}.
      </h2>
      <p className="mt-3 text-[14px] leading-[1.6] text-[var(--lib-ink-muted)] max-w-[56ch]">{line}</p>
    </div>

    <div className="hidden md:flex items-end gap-8">
      <div aria-hidden className="flex flex-col">
        <div className="flex items-end gap-[3px] px-3">
          {SPINES.map((s, i) => (
            <span
              key={i}
              className="block rounded-t-[3px]"
              style={{ width: s.w, height: s.h, background: s.c, opacity: 0.92 }}
            />
          ))}
        </div>
        <div className="h-[6px] rounded-t-sm bg-[var(--lib-gold)]" />
        <div className="h-[10px] bg-[var(--lib-navy)]/10" />
      </div>
      <figure className="mb-6 w-[220px] rounded-md border border-[var(--lib-hairline)] bg-[var(--lib-surface)] px-4 py-3">
        <blockquote className="font-serif italic text-[15px] leading-[1.4] text-[var(--lib-ink)]">
          “{quote.text}”
        </blockquote>
        <figcaption className="mt-2 text-[11px] text-[var(--lib-ink-muted)]">{quote.by}</figcaption>
      </figure>
    </div>
  </section>
);

export default GreetingBanner;
